import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import BulkCsvControls from "../components/BulkCsvControls";
import { TOPIC_UPLOAD_SAMPLE } from "../utils/csvTemplates";
import PaginationFooter from "../components/PaginationFooter";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as subjectsApi from "../api/subjects";
import * as gradesApi from "../api/grades";
import * as topicsApi from "../api/topics";
import { BULK_UPLOAD_ENDPOINT as TOPIC_BULK_UPLOAD_ENDPOINT } from "../api/topics";

export default function AdminSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_subjects");
  const { hasPermission: canEdit } = useViewPermission("edit_subjects");
  const { busy, run } = useMutationLock();
  const [topicsOfSubject, setTopicsOfSubject] = useState([]);
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [topicsModalSubjectId, setTopicsModalSubjectId] = useState(null);

  // Dropdown reference data (not paginated the same way as the main table)
  const [grades, setGrades] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");

  const fetchSubjects = async (pageArg = page) => {
    try {
      const response = await subjectsApi.list({ page: pageArg, limit });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setSubjects(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching subjects:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjectOptions = async () => {
    try {
      const response = await subjectsApi.list({ exportMode: true });
      const normalized = normalizeListResponse(response.data);
      setSubjectOptions(normalized.data);
    } catch (err) {
      console.error("Error fetching subject options:", err);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await gradesApi.list({ exportMode: true });
      const normalized = normalizeListResponse(response.data);
      setGrades(normalized.data);
    } catch (err) {
      console.error("Error fetching grades:", err);
    }
  };

  useEffect(() => {
    if (!hasPermission) return;
    fetchSubjects(1);
    fetchSubjectOptions();
    fetchGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchSubjects(newPage);
  };

  const columnKeys = ["id", "name"];

  const [name, setName] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    await run(async () => {
      try {
        if (!name) {
          notificationCtx.show({
            message: "Please add a name.",
          });
          return;
        }
        await subjectsApi.create({ name });
        setPage(1);
        await fetchSubjects(1);
        await fetchSubjectOptions();
        setName("");
        setShowSubjectForm(false);
        notificationCtx.show({
          message: "Subject added successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error adding subject:", error);
        notificationCtx.show({
          message: "Failed to add subject.",
          stat: "error",
        });
      }
    });
  };
  const [topicName, setTopicName] = useState(null);
  const addTopic = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    await run(async () => {
      try {
      const matchedSubject =
        subjectOptions.find((s) => String(s.id) === String(subject)) ||
        subjectOptions.find((s) => s.name === subject);
      const matchedGrade =
        grades.find((g) => String(g.id) === String(grade)) ||
        grades.find((g) => g.name === grade);
      const subjectId = matchedSubject?.id;
      const gradeId = matchedGrade?.id;
      const subjectName = matchedSubject?.name || subject;
      const gradeName = matchedGrade?.name || grade;
      const response = await topicsApi.create({
        topicName,
        subjectId,
        subjectName,
        gradeId,
        gradeName,
      });
      const created = response.data;
      if (created?.id && showTopicsModal) {
        setTopicsOfSubject((prev) => {
          const updated = { ...(prev || {}) };
          const gradeKey = String(created.gradeId);
          const existing = updated[gradeKey];
          updated[gradeKey] = {
            gradeName: existing?.gradeName || created.gradeName,
            topics: [...(existing?.topics || []), created],
          };
          return updated;
        });
      }
      setTopicName("");
      setGrade("");
      setSubject("");
      setShowTopicForm(false);
      notificationCtx.show({
        message: "Topic added successfully!",
        stat: "success",
      });
      } catch (error) {
        console.error("Error adding subject:", error);
        notificationCtx.show({
          message: "Failed to add subject.",
          stat: "error",
        });
      }
    });
  };

  const showTopics = async (subjectId) => {
    try {
      const response = await topicsApi.bySubject(subjectId, {
        exportMode: true,
      });
      const topics = normalizeListResponse(response.data).data;
      if (topics.length === 0) {
        notificationCtx.show({
          message: "No topics available for this subject.",
          stat: "error",
        });
        return;
      } else {
        const groupedTopics = topics.reduce((acc, topic) => {
          const gradeId = topic.gradeId;

          if (!acc[gradeId]) {
            acc[gradeId] = {
              gradeName: topic.gradeName,
              topics: [],
            };
          }

          acc[gradeId].topics.push(topic);
          return acc;
        }, {});
        setTopicsOfSubject(groupedTopics);
        setTopicsModalSubjectId(subjectId);
        setShowTopicsModal(true);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      notificationCtx.show({
        message: "No topics available for this subject.",
        stat: "error",
      });
    }
  };

  const handleDeleteTopic = (topicId) => {
    if (!canEdit) return;
    const confirmDeleteTopic = async () => {
      try {
        await topicsApi.remove(topicId);

        setTopicsOfSubject((prev) => {
          if (!prev) return prev;
          const next = {};
          Object.entries(prev).forEach(([gradeId, group]) => {
            const topics = (group.topics || []).filter(
              (t) => t.id !== topicId && t.topicId !== topicId,
            );
            if (topics.length > 0) {
              next[gradeId] = { ...group, topics };
            }
          });
          return next;
        });
        notificationCtx.show({
          message: "Topic deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete topic",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this topic?",
      stat: "info",
      onConfirm: confirmDeleteTopic,
    });
  };

  const handleDeleteSubject = (subjectId) => {
    if (!canEdit) return;
    const confirmDeleteSubject = async () => {
      try {
        await subjectsApi.remove(subjectId);

        const nextPage =
          subjects.length <= 1 && page > 1 ? page - 1 : page;
        setPage(nextPage);
        await fetchSubjects(nextPage);
        await fetchSubjectOptions();
        setShowTopicsModal(false);
        notificationCtx.show({
          message: "Subject deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete subject",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this subject?",
      stat: "info",
      onConfirm: confirmDeleteSubject,
    });
  };

  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState("");

  const handleUpdateTopicField = async (topicId, field, value) => {
    if (!canEdit) return;
    if (field === "weightage" && (value < 1 || value > 5)) {
      // Show notification
      notificationCtx.show({
        message: "Weightage must be between 1 to 5",
        stat: "error",
      });
      return;
    }
    await run(async () => {
      try {
        await topicsApi.update(topicId, { field, value });

        // Update frontend state using topicsOfSubject
        setTopicsOfSubject((prev) => {
          const updated = { ...prev };

          // Loop through all grades to find the topic
          Object.keys(updated).forEach((gradeKey) => {
            updated[gradeKey].topics = updated[gradeKey].topics.map((t) =>
              t.id === topicId ? { ...t, [field]: value } : t,
            );
          });

          return updated;
        });

        setEditingField(null);
        setEditedValue("");
      } catch (err) {
        console.error("Update failed:", err);
      }
    });
  };

  const [showSubjectForm, setShowSubjectForm] = useState(false);
  const [showTopicForm, setShowTopicForm] = useState(false);

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      {isAuthenticated && hasPermission && (
        <>
          <div
            className="d-flex flex-column"
            style={{
              height: "109vh",
              minHeight: 0,
              padding: "10px 50px 0",
              overflow: "hidden",
            }}
          >
            {/* ================= TOP HEADER ================= */}
            <div
              style={{
                backgroundColor: "#d5d1fd",
                borderRadius: "20px",
                padding: "26px 30px",
                flexShrink: 0,
                zIndex: 10,
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  marginBottom: "4px",
                }}
              >
                {/* Header Text */}
                <div>
                  <h1
                    className="mb-1"
                    style={{
                      // fontSize: "28px",
                      fontWeight: "700",
                      color: "#302d45",
                    }}
                  >
                    ALL SUBJECTS
                  </h1>

                  <small
                    style={{
                      color: "#625d75",
                    }}
                  >
                    Manage subjects and their topics
                  </small>
                </div>

                {/* Header Actions */}
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <BulkCsvControls
                    canEdit={canEdit}
                    sampleRows={TOPIC_UPLOAD_SAMPLE}
                    templateFilename="topics_upload_template.csv"
                    endpoint={TOPIC_BULK_UPLOAD_ENDPOINT}
                    notificationCtx={notificationCtx}
                    onSuccess={() => {
                      if (showTopicsModal && topicsModalSubjectId) {
                        showTopics(topicsModalSubjectId);
                      }
                    }}
                  />
                  {canEdit && (
                    <button
                      className="btn"
                      onClick={() => {
                        setShowSubjectForm(true);
                      }}
                      style={{
                        height: "38px",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#fff",
                        color: "#403b55",
                        border: "none",
                        padding: "0 15px",
                        borderRadius: "8px",
                        fontWeight: "600",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      + Add Subject
                    </button>
                  )}

                  {canEdit && (
                    <button
                      className="btn"
                      onClick={() => {
                        setShowTopicForm(true);
                      }}
                      style={{
                        height: "38px",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#fff",
                        color: "#403b55",
                        border: "none",
                        padding: "0 15px",
                        borderRadius: "8px",
                        fontWeight: "600",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      + Add Topic
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ================= SPACING ================= */}
          <div style={{ height: "16px", flexShrink: 0 }} />

            {/* ================= TABLE ================= */}

            {subjects.length === 0 ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{
                  padding: "60px 20px",
                  color: "#77718b",
                }}
              >
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#403b55",
                  }}
                >
                  No subjects available
                </div>

                <small style={{ marginTop: "5px" }}>
                  There are currently no subjects to display.
                </small>
              </div>
            ) : (
              <>
                <div
                  style={{
                    marginTop: "16px",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    borderRadius: "14px",
                    border: "1px solid #eeeaf8",
                    boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      overflow: "auto",
                    }}
                  >
                    <table
                      className="table align-middle mb-0"
                      style={{
                        minWidth: "800px",
                        width: "100%",
                        background: "#fff",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                      }}
                    >
                      <thead>
                        <tr>
                          {columnKeys.map((key) => (
                            <th
                              key={key}
                              style={{
                                backgroundColor: "#d5d1fd",
                                color: "#403b55",
                                fontWeight: "600",
                                padding: "14px 16px",
                                textAlign: "center",
                                letterSpacing: "0.4px",
                                fontSize: "13px",
                                borderBottom: "1px solid #e6e3eb",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {key.toUpperCase()}
                            </th>
                          ))}

                          <th
                            style={{
                              backgroundColor: "#d5d1fd",
                              color: "#403b55",
                              fontWeight: "600",
                              padding: "14px 16px",
                              textAlign: "center",
                              letterSpacing: "0.4px",
                              fontSize: "13px",
                              borderBottom: "1px solid #e6e3eb",
                              whiteSpace: "nowrap",
                              width: "220px",
                            }}
                          >
                            ACTIONS
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {subjects.map((item, index) => (
                          <tr key={index}>
                            {columnKeys.map((key) => (
                              <td
                                key={key}
                                style={{
                                  backgroundColor: "#fff",
                                  padding: "13px 16px",
                                  textAlign: "center",
                                  fontSize: "14px",
                                  color: "#403b55",
                                  borderBottom: "1px solid #f0eef4",
                                  whiteSpace: "nowrap",
                                  verticalAlign: "middle",
                                }}
                              >
                                {item[key]}
                              </td>
                            ))}

                            <td
                              style={{
                                textAlign: "center",
                                padding: "10px 16px",
                                borderBottom: "1px solid #f0eef4",
                              }}
                            >
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <button
                                  className="btn"
                                  onClick={() => showTopics(item.id)}
                                  style={{
                                    backgroundColor: "#f9e1be",
                                    border: "none",
                                    borderRadius: "8px",
                                    color: "#62503a",
                                    fontWeight: "600",
                                    fontSize: "13px",
                                    padding: "8px 13px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  View Topics
                                </button>

                                {canEdit && (
                                  <button
                                    className="btn"
                                    onClick={() => handleDeleteSubject(item.id)}
                                    style={{
                                      backgroundColor: "#f2c4c4",
                                      border: "none",
                                      borderRadius: "8px",
                                      width: "38px",
                                      height: "34px",
                                      padding: 0,
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-trash"
                                      style={{
                                        fontSize: "14px",
                                        color: "#654343",
                                      }}
                                    />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ================= FOOTER ================= */}

                <PaginationFooter
                  page={page}
                  limit={limit}
                  total={total}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  itemLabel="subjects"
                />
              </>
            )}

            {/* ================= ADD SUBJECT MODAL ================= */}

            {canEdit && showSubjectForm && (
              <div
                className="modal show"
                style={{
                  display: "block",
                  backgroundColor: "rgba(48,45,69,0.45)",
                }}
                tabIndex="-1"
              >
                <div
                  className="modal-dialog modal-dialog-centered"
                  style={{
                    maxWidth: "520px",
                  }}
                >
                  <div
                    className="modal-content"
                    style={{
                      border: "none",
                      borderRadius: "18px",
                      overflow: "hidden",
                      boxShadow: "0 10px 35px rgba(48,45,69,0.18)",
                    }}
                  >
                    {/* Modal Header */}
                    <div
                      style={{
                        padding: "22px 24px",
                        backgroundColor: "#d5d1fd",
                        borderBottom: "1px solid rgba(80,70,110,0.08)",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h4
                            className="mb-1"
                            style={{
                              fontWeight: "700",
                              color: "#302d45",
                            }}
                          >
                            Add Subject
                          </h4>

                          <small
                            style={{
                              color: "#625d75",
                            }}
                          >
                            Create a new subject
                          </small>
                        </div>

                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowSubjectForm(false)}
                          aria-label="Close"
                        />
                      </div>
                    </div>

                    {/* Modal Body */}
                    <div
                      style={{
                        padding: "24px",
                        backgroundColor: "#fff",
                      }}
                    >
                      <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                          <label
                            htmlFor="subjectName"
                            className="form-label"
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#403b55",
                            }}
                          >
                            Subject Name
                          </label>

                          <input
                            id="subjectName"
                            type="text"
                            name="subject"
                            value={name || ""}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="form-control"
                            placeholder="Enter Subject Name"
                            style={{
                              border: "1px solid #e5e1ef",
                              borderRadius: "9px",
                              padding: "10px 13px",
                              color: "#403b55",
                              backgroundColor: "#fff",
                            }}
                          />
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setShowSubjectForm(false)}
                            style={{
                              backgroundColor: "#f3f1f7",
                              border: "none",
                              color: "#625d75",
                              fontWeight: "600",
                              padding: "9px 18px",
                              borderRadius: "8px",
                            }}
                          >
                            Cancel
                          </button>

                          <button
                            type="submit"
                            className="btn"
                            disabled={busy || !name}
                            style={{
                              backgroundColor: !name ? "#f3f1f7" : "#f2c4c4",
                              border: "none",
                              color: !name ? "#aaa5b5" : "#654343",
                              fontWeight: "600",
                              padding: "9px 20px",
                              borderRadius: "8px",
                            }}
                          >
                            {busy ? "Working..." : "Add Subject"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================= ADD TOPIC MODAL ================= */}

            {canEdit && showTopicForm && (
              <div
                className="modal show"
                style={{
                  display: "block",
                  backgroundColor: "rgba(48,45,69,0.45)",
                }}
                tabIndex="-1"
              >
                <div
                  className="modal-dialog modal-dialog-centered"
                  style={{
                    maxWidth: "520px",
                  }}
                >
                  <div
                    className="modal-content"
                    style={{
                      border: "none",
                      borderRadius: "18px",
                      overflow: "hidden",
                      boxShadow: "0 10px 35px rgba(48,45,69,0.18)",
                    }}
                  >
                    {/* Modal Header */}
                    <div
                      style={{
                        padding: "22px 24px",
                        backgroundColor: "#d5d1fd",
                        borderBottom: "1px solid rgba(80,70,110,0.08)",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h4
                            className="mb-1"
                            style={{
                              fontWeight: "700",
                              color: "#302d45",
                            }}
                          >
                            Add Topic
                          </h4>

                          <small
                            style={{
                              color: "#625d75",
                            }}
                          >
                            Add a topic to a subject
                          </small>
                        </div>

                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowTopicForm(false)}
                          aria-label="Close"
                        />
                      </div>
                    </div>

                    {/* Modal Body */}
                    <div
                      style={{
                        padding: "24px",
                        backgroundColor: "#fff",
                      }}
                    >
                      <form onSubmit={addTopic}>
                        <div className="mb-3">
                          <label
                            className="form-label"
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#403b55",
                            }}
                          >
                            Grade
                          </label>

                          <select
                            value={grade}
                            onChange={(e) => setGrade(e.target.value)}
                            className="form-select"
                            required
                            style={{
                              border: "1px solid #e5e1ef",
                              borderRadius: "9px",
                              padding: "10px 13px",
                              color: "#403b55",
                              backgroundColor: "#fff",
                            }}
                          >
                            <option value="" disabled>
                              Select Grade
                            </option>

                            {grades.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-3">
                          <label
                            className="form-label"
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#403b55",
                            }}
                          >
                            Subject
                          </label>

                          <select
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="form-select"
                            required
                            style={{
                              border: "1px solid #e5e1ef",
                              borderRadius: "9px",
                              padding: "10px 13px",
                              color: "#403b55",
                              backgroundColor: "#fff",
                            }}
                          >
                            <option value="" disabled>
                              Select Subject
                            </option>

                            {subjectOptions.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-4">
                          <label
                            htmlFor="topicName"
                            className="form-label"
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#403b55",
                            }}
                          >
                            Topic Name
                          </label>

                          <input
                            id="topicName"
                            type="text"
                            name="topic"
                            value={topicName || ""}
                            onChange={(e) => setTopicName(e.target.value)}
                            required
                            className="form-control"
                            placeholder="Enter Topic Name"
                            style={{
                              border: "1px solid #e5e1ef",
                              borderRadius: "9px",
                              padding: "10px 13px",
                              color: "#403b55",
                              backgroundColor: "#fff",
                            }}
                          />
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setShowTopicForm(false)}
                            style={{
                              backgroundColor: "#f3f1f7",
                              border: "none",
                              color: "#625d75",
                              fontWeight: "600",
                              padding: "9px 18px",
                              borderRadius: "8px",
                            }}
                          >
                            Cancel
                          </button>

                          <button
                            type="submit"
                            className="btn"
                            disabled={busy || !grade || !subject || !topicName}
                            style={{
                              backgroundColor:
                                !grade || !subject || !topicName
                                  ? "#f3f1f7"
                                  : "#f2c4c4",
                              border: "none",
                              color:
                                !grade || !subject || !topicName
                                  ? "#aaa5b5"
                                  : "#654343",
                              fontWeight: "600",
                              padding: "9px 20px",
                              borderRadius: "8px",
                            }}
                          >
                            {busy ? "Working..." : "Add Topic"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {showTopicsModal && (
            <div
              className="modal show"
              style={{
                display: "block",
                backgroundColor: "rgba(48,45,69,0.45)",
              }}
              tabIndex="-1"
            >
              <div
                className="modal-dialog modal-xl modal-dialog-centered"
                style={{
                  maxWidth: "1100px",
                  maxHeight: "100vh",
                  margin: "1.75rem auto",
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    border: "none",
                    borderRadius: "18px",
                    overflow: "hidden",
                    boxShadow: "0 10px 35px rgba(48,45,69,0.18)",
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* ================= MODAL HEADER ================= */}

                  <div
                    style={{
                      padding: "22px 24px",
                      backgroundColor: "#d5d1fd",
                      borderBottom: "1px solid rgba(80,70,110,0.08)",
                      flexShrink: 0,
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4
                          className="mb-1"
                          style={{
                            fontWeight: "700",
                            color: "#302d45",
                          }}
                        >
                          Subject Topics
                        </h4>

                        {/* <small
                          style={{
                            color: "#625d75",
                          }}
                        >
                          View and manage topics grouped by grade
                        </small> */}
                      </div>

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowTopicsModal(false)}
                        aria-label="Close"
                      />
                    </div>

                    {/* Weightage Information */}
                    <div
                      className="d-flex align-items-center"
                      style={{
                        marginTop: "14px",
                        padding: "9px 12px",
                        backgroundColor: "rgba(255,255,255,0.65)",
                        borderRadius: "8px",
                        width: "fit-content",
                      }}
                    >
                      <i
                        className="fa-solid fa-circle-info"
                        style={{
                          fontSize: "13px",
                          color: "#625d75",
                          marginRight: "8px",
                        }}
                      />

                      <span
                        style={{
                          fontSize: "12px",
                          color: "#625d75",
                          fontWeight: "500",
                        }}
                      >
                        Weightage must be 1–5
                        <span
                          style={{
                            marginLeft: "4px",
                            color: "#77718b",
                          }}
                        >
                          (1 = easy, 5 = difficult)
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* ================= MODAL BODY ================= */}

                  <div
                    style={{
                      padding: "24px",
                      backgroundColor: "#f8f7fb",
                      overflowY: "auto",
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <div className="row g-4">
                      {Object.values(topicsOfSubject).map((grade) => (
                        <div key={grade.gradeName} className="col-md-6">
                          {/* Grade Card */}

                          <div
                            style={{
                              backgroundColor: "#fff",
                              borderRadius: "14px",
                              border: "1px solid #eeeaf8",
                              boxShadow: "0 2px 8px rgba(48,45,69,0.04)",
                              overflow: "hidden",
                            }}
                          >
                            {/* Grade Header */}

                            <div
                              className="d-flex justify-content-between align-items-center"
                              style={{
                                padding: "14px 16px",
                                backgroundColor: "#f1eefb",
                                borderBottom: "1px solid #e8e4f0",
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#77718b",
                                    fontWeight: "500",
                                    marginBottom: "2px",
                                  }}
                                >
                                  GRADE
                                </div>

                                <h5
                                  className="mb-0"
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "700",
                                    color: "#403b55",
                                  }}
                                >
                                  {grade.gradeName}
                                </h5>
                              </div>

                              <span
                                style={{
                                  backgroundColor: "#fff",
                                  color: "#77718b",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  padding: "5px 9px",
                                  borderRadius: "7px",
                                }}
                              >
                                {grade.topics.length}{" "}
                                {grade.topics.length === 1 ? "Topic" : "Topics"}
                              </span>
                            </div>

                            {/* Topics Table */}

                            <div
                              style={{
                                overflowX: "auto",
                              }}
                            >
                              <table
                                className="table align-middle mb-0"
                                style={{
                                  width: "100%",
                                  backgroundColor: "#fff",
                                  borderCollapse: "separate",
                                  borderSpacing: 0,
                                }}
                              >
                                <thead>
                                  <tr>
                                    <th
                                      style={{
                                        width: "12%",
                                        padding: "11px 14px",
                                        backgroundColor: "#fff",
                                        color: "#77718b",
                                        fontSize: "11px",
                                        fontWeight: "700",
                                        letterSpacing: "0.5px",
                                        borderBottom: "1px solid #eeeaf2",
                                        textAlign: "center",
                                      }}
                                    >
                                      ID
                                    </th>

                                    <th
                                      style={{
                                        width: "53%",
                                        padding: "11px 14px",
                                        backgroundColor: "#fff",
                                        color: "#77718b",
                                        fontSize: "11px",
                                        fontWeight: "700",
                                        letterSpacing: "0.5px",
                                        borderBottom: "1px solid #eeeaf2",
                                      }}
                                    >
                                      TOPIC
                                    </th>

                                    <th
                                      style={{
                                        width: "20%",
                                        padding: "11px 14px",
                                        backgroundColor: "#fff",
                                        color: "#77718b",
                                        fontSize: "11px",
                                        fontWeight: "700",
                                        letterSpacing: "0.5px",
                                        borderBottom: "1px solid #eeeaf2",
                                        textAlign: "center",
                                      }}
                                    >
                                      WEIGHT
                                    </th>

                                    <th
                                      style={{
                                        width: "15%",
                                        padding: "11px 14px",
                                        backgroundColor: "#fff",
                                        color: "#77718b",
                                        fontSize: "11px",
                                        fontWeight: "700",
                                        letterSpacing: "0.5px",
                                        borderBottom: "1px solid #eeeaf2",
                                        textAlign: "center",
                                      }}
                                    >
                                      ACTION
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {grade.topics.map((topic, index) => (
                                    <tr key={topic.id}>
                                      {(() => {
                                        const isEditing = (topicId, field) =>
                                          editingField?.topicId === topicId &&
                                          editingField?.field === field;

                                        return (
                                          <>
                                            {/* ================= TOPIC ID ================= */}

                                            <td
                                              style={{
                                                padding: "12px 14px",
                                                borderBottom:
                                                  "1px solid #f0eef4",
                                                textAlign: "center",
                                                verticalAlign: "middle",
                                                color: "#625d75",
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                fontVariantNumeric: "tabular-nums",
                                              }}
                                            >
                                              {topic.id}
                                            </td>

                                            {/* ================= TOPIC NAME ================= */}

                                            <td
                                              style={{
                                                padding: "12px 14px",
                                                borderBottom:
                                                  "1px solid #f0eef4",
                                                color: "#403b55",
                                                fontSize: "13px",
                                                verticalAlign: "middle",
                                              }}
                                            >
                                              <div className="d-flex align-items-center">
                                                {canEdit &&
                                                isEditing(
                                                  topic.id,
                                                  "topicName",
                                                ) ? (
                                                  <input
                                                    type="text"
                                                    value={editedValue}
                                                    onChange={(e) =>
                                                      setEditedValue(
                                                        e.target.value,
                                                      )
                                                    }
                                                    className="form-control form-control-sm"
                                                    style={{
                                                      border:
                                                        "1px solid #e5e1ef",
                                                      borderRadius: "7px",
                                                      color: "#403b55",
                                                      marginRight: "8px",
                                                    }}
                                                  />
                                                ) : (
                                                  <span
                                                    style={{
                                                      fontWeight: "500",
                                                      lineHeight: "1.4",
                                                      flex: 1,
                                                    }}
                                                  >
                                                    {topic.topicName}
                                                  </span>
                                                )}

                                                {canEdit && (
                                                  <button
                                                    className="btn"
                                                    onClick={() => {
                                                      if (
                                                        isEditing(
                                                          topic.id,
                                                          "topicName",
                                                        )
                                                      ) {
                                                        handleUpdateTopicField(
                                                          topic.id,
                                                          "topicName",
                                                          editedValue,
                                                          index,
                                                        );
                                                      } else {
                                                        setEditingField({
                                                          topicId: topic.id,
                                                          field: "topicName",
                                                        });

                                                        setEditedValue(
                                                          topic.topicName,
                                                        );
                                                      }
                                                    }}
                                                    style={{
                                                      backgroundColor:
                                                        isEditing(
                                                          topic.id,
                                                          "topicName",
                                                        )
                                                          ? "#bff1db"
                                                          : "#f3f1f7",
                                                      border: "none",
                                                      borderRadius: "7px",
                                                      width: "30px",
                                                      height: "30px",
                                                      padding: 0,
                                                      flexShrink: 0,
                                                    }}
                                                  >
                                                    <i
                                                      className={
                                                        "fa-solid fa-" +
                                                        (isEditing(
                                                          topic.id,
                                                          "topicName",
                                                        )
                                                          ? "check"
                                                          : "pencil")
                                                      }
                                                      style={{
                                                        color: "#403b55",
                                                        fontSize: "12px",
                                                      }}
                                                    />
                                                  </button>
                                                )}
                                              </div>
                                            </td>

                                            {/* ================= WEIGHTAGE ================= */}

                                            <td
                                              style={{
                                                padding: "12px 14px",
                                                borderBottom:
                                                  "1px solid #f0eef4",
                                                textAlign: "center",
                                                verticalAlign: "middle",
                                              }}
                                            >
                                              <div className="d-flex align-items-center justify-content-center">
                                                {canEdit &&
                                                isEditing(
                                                  topic.id,
                                                  "weightage",
                                                ) ? (
                                                  <input
                                                    type="number"
                                                    min={1}
                                                    max={5}
                                                    value={editedValue}
                                                    onChange={(e) => {
                                                      setEditedValue(
                                                        e.target.value,
                                                      );
                                                    }}
                                                    className="form-control form-control-sm"
                                                    style={{
                                                      width: "58px",
                                                      border:
                                                        "1px solid #e5e1ef",
                                                      borderRadius: "7px",
                                                      textAlign: "center",
                                                      marginRight: "7px",
                                                    }}
                                                  />
                                                ) : (
                                                  <span
                                                    style={{
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      minWidth: "28px",
                                                      height: "28px",
                                                      backgroundColor:
                                                        "#f9e1be",
                                                      borderRadius: "7px",
                                                      color: "#62503a",
                                                      fontSize: "12px",
                                                      fontWeight: "700",
                                                      marginRight: "7px",
                                                    }}
                                                  >
                                                    {topic.weightage}
                                                  </span>
                                                )}

                                                {canEdit && (
                                                  <button
                                                    className="btn"
                                                    onClick={() => {
                                                      if (
                                                        isEditing(
                                                          topic.id,
                                                          "weightage",
                                                        )
                                                      ) {
                                                        handleUpdateTopicField(
                                                          topic.id,
                                                          "weightage",
                                                          editedValue,
                                                          index,
                                                        );
                                                      } else {
                                                        setEditingField({
                                                          topicId: topic.id,
                                                          field: "weightage",
                                                        });

                                                        setEditedValue(
                                                          topic.weightage,
                                                        );
                                                      }
                                                    }}
                                                    style={{
                                                      backgroundColor:
                                                        isEditing(
                                                          topic.id,
                                                          "weightage",
                                                        )
                                                          ? "#bff1db"
                                                          : "#f3f1f7",
                                                      border: "none",
                                                      borderRadius: "7px",
                                                      width: "30px",
                                                      height: "30px",
                                                      padding: 0,
                                                    }}
                                                  >
                                                    <i
                                                      className={
                                                        "fa-solid fa-" +
                                                        (isEditing(
                                                          topic.id,
                                                          "weightage",
                                                        )
                                                          ? "check"
                                                          : "pencil")
                                                      }
                                                      style={{
                                                        color: "#403b55",
                                                        fontSize: "12px",
                                                      }}
                                                    />
                                                  </button>
                                                )}
                                              </div>
                                            </td>

                                            {/* ================= DELETE ================= */}

                                            <td
                                              style={{
                                                padding: "12px 14px",
                                                borderBottom:
                                                  "1px solid #f0eef4",
                                                textAlign: "center",
                                                verticalAlign: "middle",
                                              }}
                                            >
                                              {canEdit && (
                                                <button
                                                  onClick={() =>
                                                    handleDeleteTopic(topic.id)
                                                  }
                                                  className="btn"
                                                  style={{
                                                    backgroundColor: "#f2c4c4",
                                                    border: "none",
                                                    borderRadius: "7px",
                                                    width: "34px",
                                                    height: "32px",
                                                    padding: 0,
                                                  }}
                                                >
                                                  <i
                                                    className="fa-solid fa-trash"
                                                    style={{
                                                      fontSize: "12px",
                                                      color: "#654343",
                                                    }}
                                                  />
                                                </button>
                                              )}
                                            </td>
                                          </>
                                        );
                                      })()}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
